/*! JointJS+ v4.1.0 - HTML5 Diagramming Framework

Copyright (c) 2024 client IO

 2024-12-04


This Source Code Form is subject to the terms of the JointJS+ License
, v. 2.0. If a copy of the JointJS+ License was not distributed with this
file, You can obtain one at https://www.jointjs.com/license
 or from the JointJS+ archive as was distributed by client IO. See the LICENSE file.*/

// @ts-nocheck
import * as joint from '@joint/plus';
import { inspectorDefinitions } from '../config/inspector';
const HIDDEN_CLASS_NAME = 'hidden';
const OPEN_CLASS_NAME = '--view-node';

type InspectorElements = {
    openGroupsButton: HTMLButtonElement;
    closeGroupsButton: HTMLButtonElement;
    container: HTMLElement;
    header: HTMLDivElement;
    content: HTMLElement;
    jointApp: HTMLElement;
    uploadImage: Function;
    tabsList: Function;
    showEditor: Function;
    showElementOpportunityEditor: Function;
    view: string;
    getSelectedCustomProperty: Function;
}
export class InspectorService {

    inspector: joint.ui.Inspector;
    private readonly header: HTMLDivElement;
    private readonly content: HTMLElement;
    private readonly container: HTMLElement;
    private readonly jointApp: HTMLElement;
    private uploadImage: Function
    private readonly tabsList: Function
    private readonly showEditor: Function
    private readonly showOpportunityEditor: Function
    private readonly view: string
    private readonly getSelectedCustomProperty: Function

    constructor({
        openGroupsButton,
        closeGroupsButton,
        container,
        header,
        content,
        jointApp,
        uploadImage,
        tabsList,
        showEditor,
        showElementOpportunityEditor: showOpportunityEditor,
        view,
        getSelectedCustomProperty
    }: InspectorElements) {
        openGroupsButton.addEventListener('click', () => this.inspector?.openGroups());
        closeGroupsButton.addEventListener('click', () => this.inspector?.closeGroups());
        this.header = header;
        this.content = content;
        this.container = container;
        this.jointApp = jointApp;
        this.uploadImage = uploadImage;
        this.tabsList = tabsList;
        this.showEditor = showEditor;
        this.showOpportunityEditor = showOpportunityEditor;
        this.view = view;
        this.getSelectedCustomProperty = getSelectedCustomProperty;
    }

    create(cell: joint.dia.Cell): joint.ui.Inspector {
        this.header.classList.remove(HIDDEN_CLASS_NAME);
        this.container.classList.remove(HIDDEN_CLASS_NAME);
        this.jointApp.classList.add(OPEN_CLASS_NAME);
        const { groups, inputs } = this.getInspectorConfig()[cell.get('type')];
        const cellData = cell.get('data') || {};
        this.appendTabSelection(cell, inputs);
        const inspector = joint.ui.Inspector.create(this.content, {
            cell,
            groups,
            inputs,
            container: this.container,
            renderFieldContent: (options, path, _value, inspector) => {
                if (options.type === 'image-picker') {
                    const label = document.createElement('label');
                    label.textContent = options.label;

                    const input = document.createElement('input');

                    input.type = 'file';
                    input.accept = 'image/x-png,image/gif,image/jpeg';

                    const field = document.createElement('div');
                    field.appendChild(label);
                    field.appendChild(input);

                    input.addEventListener('change', function() {
                        inspector.updateCell(field, path, options);
                    });

                    return field;
                }

                if (options.type === 'rich-text') {
                  const editor = document.createElement('div');
                  editor.classList.add(...['joint-text-editor', 'ql-editor']);
                  editor.setAttribute('contenteditable', true);
                  editor.innerHTML = cell.prop(path) || '';
                  const editbtn = document.createElement('a');
                  editbtn.classList.add('joint-text-editor__edit');
                  editbtn.textContent = 'Open Editor';
                  editbtn.addEventListener('click', () => {
                    this.showEditor(cell, path);
                  });
                  const field = document.createElement('div');
                  field.classList.add('ql-snow');
                  field.appendChild(editor);
                  field.appendChild(editbtn);
                  editor.addEventListener('input', function() {
                      inspector.updateCell(field, path, options);
                  });
                  return field;
                }

                if (options.type === 'opportunity-description') {
                    const field = document.createElement('div');
                    field.style.marginTop = '10px';
                    const editbtn = document.createElement('button');
                    editbtn.classList.add('btn', 'btn-sm', 'btn-info2', 'text-white');
                    editbtn.textContent = 'Open Editor';
                    editbtn.style.width = '100%';
                    editbtn.addEventListener('click', () => {
                        this.showOpportunityEditor(cell);
                    });
                    field.appendChild(editbtn);
                    return field;
                }

                // Use the default field renderer.
                return null;
            },
            getFieldValue: (field, type) => {
                if (type === 'image-picker') {
                    const file = field.querySelector<HTMLInputElement>('input').files.item(0);
                    this.uploadImage(file, cell)
                    return { value: file ? 'img.src' : '' };
                }
                if (type === 'rich-text') {
                  return { value: field.querySelector('.joint-text-editor')?.innerHTML};
                }

                // Use the default field value getter.
                return null;
            }
        });

        if (this.inspector !== inspector) {
            inspector.on('close', () => {
                this.header.classList.add(HIDDEN_CLASS_NAME);
                this.container.classList.add(HIDDEN_CLASS_NAME);
                this.jointApp.classList.remove(OPEN_CLASS_NAME);
            });
            this.inspector = inspector;
        }

        // Listen for changes to cell data (especially customProperty) to update button disabled state
        let previousCustomProperty = cellData.customProperty;
        const refreshInspector = () => {
            if (this.inspector && this.inspector.options.cell === cell) {
                const currentData = cell.get('data') || {};
                const currentCustomProperty = currentData.customProperty;
                // Only refresh if customProperty actually changed
                if (previousCustomProperty !== currentCustomProperty) {
                    previousCustomProperty = currentCustomProperty;
                    // Recreate inspector to update button disabled state
                    setTimeout(() => {
                        if (this.inspector) {
                            this.inspector.close();
                        }
                        this.create(cell);
                    }, 100);
                }
            }
        };

        // Remove previous listeners if any
        cell.off('change:data', refreshInspector);
        // Add listener for data changes (specifically customProperty)
        cell.on('change:data', refreshInspector);

        // Also listen to inspector's updateCell to catch dropdown changes immediately
        const originalUpdateCell = inspector.updateCell.bind(inspector);
        inspector.updateCell = (field?: any, path?: string, opt?: any) => {
            const result = originalUpdateCell(field, path, opt);
            // If customProperty was updated, refresh inspector to update button state
            if (path && (path === 'data/customProperty' || path.includes('customProperty'))) {
                setTimeout(() => {
                    refreshInspector();
                }, 200);
            }
            return result;
        };

        return this.inspector;
    }

    getInspectorConfig() {
        return inspectorDefinitions;
    }

    appendTabSelection(cell: joint.dia.Cell, inputs: object) {
        const cellData = cell.get('data') || {};
        const customProperty = cellData.customProperty;
        // Only show Open Editor if opportunity type is actually selected (not empty/null/undefined)
        const hasOpportunityProperty = customProperty != null &&
            customProperty !== '' &&
            customProperty !== undefined &&
            String(customProperty).trim() !== '' &&
            String(customProperty).includes('Opportunity');

        const isPresentationMode = this.view === 'presentation';
        let shouldHideTabNavigation = false;

        if (isPresentationMode) {
            // On presentation page, check if element is currently highlighted
            const selectedCustomProperty = this.getSelectedCustomProperty();
            const isHighlighted = hasOpportunityProperty &&
                selectedCustomProperty &&
                selectedCustomProperty !== '' &&
                customProperty === selectedCustomProperty;
            shouldHideTabNavigation = isHighlighted;
        }
        // On input page, always show Navigate to Tab (shouldHideTabNavigation remains false)

        if (!(cell?.attributes?.attrs?.meta?.type) &&
            !['app.Link', 'app.Line'].includes(cell.get('type')) &&
            !shouldHideTabNavigation) {
            inputs.attrs.meta = {
                tab: {
                    type: 'select-box',
                    options: this.tabsList(),
                    label: 'Navigate to Tab',
                    placeholder: 'Select',
                    group: 'presentation',
                    index: 0
                }
            };
        } else {
            delete inputs.attrs.meta;
        }

        // Ensure inputs.data exists
        if (!inputs.data) {
            inputs.data = {};
        }

        // Always show opportunity description editor button
        inputs.data.elementOpportunityDescription = {
            type: 'opportunity-description',
            label: 'Opportunity Description',
            group: 'customProperties',
            index: 2
        };
    }
}


